import { BUSINESS_ID, SITE_URL, business } from './business';
import type { Dish } from './dishes';

/**
 * Structured data for a single dish page.
 *
 * Three separate entities rather than one blob:
 *  - the dish as a MenuItem hanging off the ONE canonical Restaurant node, so
 *    the dish is associated with this address instead of creating a second
 *    business entity that competes with the home page;
 *  - the breadcrumb trail, which matches the visible breadcrumb;
 *  - the FAQ, every question of which is rendered on the page.
 */
export const buildDishSchemas = (dish: Dish) => {
  const canonical = `${SITE_URL}/dishes/${dish.slug}`;

  const menuItemSchema = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    '@id': BUSINESS_ID,
    name: business.name,
    url: SITE_URL,
    hasMenu: {
      '@type': 'Menu',
      '@id': `${SITE_URL}/menu#menu`,
      hasMenuSection: {
        '@type': 'MenuSection',
        name: dish.menuSection,
        hasMenuItem: {
          '@type': 'MenuItem',
          name: dish.menuName,
          ...(dish.menuName !== dish.name ? { alternateName: dish.name } : {}),
          description: dish.menuDescription,
          url: canonical,
          offers: {
            '@type': 'Offer',
            price: dish.basePrice.toFixed(2),
            priceCurrency: 'USD',
          },
        },
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Signature Dishes', item: `${SITE_URL}/dishes` },
      { '@type': 'ListItem', position: 3, name: dish.name, item: canonical },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: dish.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return [menuItemSchema, breadcrumbSchema, faqSchema];
};
